import * as React from 'react';

import { Button } from '../../atoms/button/button';
import { ICON_BG, type IconBgTone } from '../gamification-widgets/gamification-widgets';

export interface RecommendationCardProps {
	/** Icona della raccomandazione, dentro il quadrato tinto. */
	icon: React.ReactNode;
	/** Colore di sfondo dell'icona. Default: `accentSoft`. */
	iconTone?: IconBgTone;
	/** Cosa fare, in una riga. */
	title: React.ReactNode;
	/** Quando conviene farlo — a destra del titolo, attenuato. */
	when?: React.ReactNode;
	/** Perché conviene: due righe al massimo, sotto il titolo. */
	desc: React.ReactNode;
	/** Risposta «l'ho fatta» / «non la faccio». */
	onAnswer: (answer: 'si' | 'no') => void;
}

/**
 * Raccomandazione AI con la coppia di risposta ✕ No / ✓ Sì (mock v11).
 *
 * Diversa da {@link IncentiveRow}, che è una riga di elenco senza azioni: qui
 * ogni voce è un riquadro a sé perché chiede una risposta, e la risposta è ciò
 * che alimenta il consuntivo del risparmio adottato. Il riquadro sparisce
 * quando la risposta è data — la card che lo ospita mostra solo le pendenti.
 */
const RecommendationCard = ({
	icon,
	iconTone = 'accentSoft',
	title,
	when,
	desc,
	onAnswer,
}: RecommendationCardProps) => (
	<div className="rounded-[9px] border border-border bg-card px-[11px] py-2.5">
		<div className="flex items-start gap-[9px]">
			<div className="inc-icon" style={{ background: ICON_BG[iconTone] }}>
				{icon}
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-start justify-between gap-1.5">
					<div className="text-[12px] font-bold leading-[1.3]">{title}</div>
					{when && (
						<span className="shrink-0 whitespace-nowrap font-mono text-[9px] text-text-muted">
							{when}
						</span>
					)}
				</div>
				<div className="mt-1 text-[11px] leading-[1.4] text-text-muted">{desc}</div>
				<div className="mt-[9px] flex gap-1.5">
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="h-auto flex-1 py-1 text-[11px]"
						onClick={() => onAnswer('no')}
					>
						✕ No
					</Button>
					<Button
						type="button"
						variant="successSoft"
						size="sm"
						className="h-auto flex-1 py-1 text-[11px]"
						onClick={() => onAnswer('si')}
					>
						✓ Sì
					</Button>
				</div>
			</div>
		</div>
	</div>
);
RecommendationCard.displayName = 'RecommendationCard';

export { RecommendationCard };
